import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import Dropdown, { DropdownGroup } from '../ui/Dropdown'
import GlobalSearch from './GlobalSearch'
import { BrandGlyph } from '../components/BrandMark'
import { Icon } from '../ui/primitives'
import { useToast } from '../ui/Toast'
import { useTheme } from '../theme/ThemeProvider'
import { useAuth } from '../auth/AuthContext'
import { isSystemAdministrator } from '../auth/permissions'
import { qk } from '../lib/queryKeys'
import { friendlyAuthError } from '../lib/errors'
import { formatRole } from '../lib/format'
import { replayTour, roleHasTour } from '../tour/tours'
import * as notificationsApi from '../api/notifications'
import * as usersApi from '../api/users'
import * as rolesApi from '../api/roles'

function initialsFromName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** The signed-in user's photo (My Profile upload), initials until one loads. */
function UserAvatar({ name, src, className = '' }) {
  const [failedSrc, setFailedSrc] = useState(null)
  const showPhoto = Boolean(src) && failedSrc !== src
  return (
    <span className={`avatar avatar-grad${showPhoto ? ' avatar-photo' : ''} ${className}`}>
      {showPhoto ? <img src={src} alt="" onError={() => setFailedSrc(src)} /> : initialsFromName(name)}
    </span>
  )
}

export default function Topbar({ onOpenNav, searchRef }) {
  const { theme, toggleTheme } = useTheme()
  const { user, signOut, can, preview, startPreview, stopPreview } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const canApproveUsers = can('settings_approvals.approve')
  /* Signups only (not requisitions or offers): email confirmed, waiting in
     Settings → Approvals. Shown to whoever can approve them, 0 included (grey).
     Same key as that tab. */
  const { data: readySignups = [] } = useQuery({
    queryKey: qk.users.pendingApprovals(),
    queryFn: () => usersApi.listPendingApprovals().then((r) => r.data ?? []),
    select: (rows) => rows.filter((u) => u.is_active),
    enabled: canApproveUsers,
    refetchInterval: 60_000,
  })
  const signupsPending = readySignups.length

  const notifQuery = useQuery({
    queryKey: qk.notifications.list({ top: 6 }),
    queryFn: notificationsApi.fetchFeed,
    staleTime: 0,
    refetchInterval: 15_000,
  })
  const notifications = notifQuery.data?.items ?? []
  const unread = notifQuery.data?.unread ?? 0

  const markAll = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onError: (err) => err?.alerted || toast(friendlyAuthError(err, 'Could not mark all as read.'), 'error'),
    onSuccess: () => toast('All notifications marked as read', 'success'),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.notifications.all() }),
  })

  const markOne = useMutation({
    mutationFn: (id) => notificationsApi.markRead(id),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.notifications.all() }),
  })

  // View as role: System Administrator only, and only when not already viewing as
  // someone. While viewing as a role the menu is that role's menu (just the role,
  // no list) plus Exit role view — exit first to view a different role.
  const canPreview = !preview && can('role_preview.view') && isSystemAdministrator(user?.role_name)
  const realRoleId = preview?.real_role_id ?? user?.role_id
  const [rolesOpen, setRolesOpen] = useState(false)
  const [switchingTo, setSwitchingTo] = useState(null)
  const rolesQuery = useQuery({
    queryKey: qk.roles.preview(),
    meta: { globalLoader: false }, // background — never blur the screen
    queryFn: () => rolesApi.listPreviewRoles().then((r) => (Array.isArray(r?.data) ? r.data : [])),
    enabled: canPreview && rolesOpen,
    staleTime: 60_000,
  })

  async function pickRole(role, close) {
    if (role.id === user?.role_id) return close()
    if (role.id === realRoleId) return stopPreview()
    setSwitchingTo(role.id)
    try {
      await startPreview(role.id)
    } catch (err) {
      setSwitchingTo(null)
      if (!err?.alerted) toast(friendlyAuthError(err, 'Could not open that role.'), 'error')
    }
  }

  /* Phone header: search folds into an icon; opening it lays the search field
     over the bar. Any navigation (a result picked) folds it away again. */
  const location = useLocation()
  const [searchOpen, setSearchOpen] = useState(false)
  useEffect(() => { setSearchOpen(false) }, [location.pathname])
  function openSearch() {
    setSearchOpen(true)
    requestAnimationFrame(() => searchRef?.current?.focus())
  }

  const name = user?.name || 'Guest'
  const email = user?.email || ''
  const role = formatRole(user?.role_name || user?.role) || 'Member'

  function openNotif(n) {
    if (n.unread) markOne.mutate(n.id)
    if (n.linkPath) navigate(n.linkPath)
    else navigate('/notifications')
  }

  return (
    <header className={`topbar${searchOpen ? ' search-open' : ''}`}>
      <button className="icon-btn menu-toggle" onClick={onOpenNav} aria-label="Toggle menu">
        <Icon name="menu" />
      </button>

      {/* Phones only (mobile.css): app-style header — brand on the left, and
          search behind an icon that opens the same GlobalSearch over the bar. */}
      <Link to="/" className="topbar-brand" aria-label="TalentFlow home">
        <span className="brand-logo"><BrandGlyph /></span>
        <span className="topbar-brand-text">
          <span className="topbar-brand-name">TalentFlow</span>
          <span className="topbar-brand-sub">Utopia Brands · ATS</span>
        </span>
      </Link>

      <GlobalSearch inputRef={searchRef} />
      {searchOpen && (
        <button type="button" className="icon-btn tb-search-close" onClick={() => setSearchOpen(false)} aria-label="Close search">
          <Icon name="x" />
        </button>
      )}

      <div className="topbar-actions">
        <button type="button" className="icon-btn tb-search-btn" onClick={openSearch} aria-label="Search">
          <Icon name="search" />
        </button>
        <button
          className="icon-btn tb-theme"
          onClick={toggleTheme}
          aria-pressed={theme === 'dark'}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <span className="icon-sun"><Icon name="sun" /></span>
          <span className="icon-moon"><Icon name="moon" /></span>
        </button>

        {/* Always shown to whoever can approve users, with 0 when nobody is waiting. */}
        {canApproveUsers && (
          // A labelled pill with the count always visible, so it reads as
          // "approvals waiting" rather than an "add member" action. Names are
          // in the tooltip.
          <Link
            to="/settings?tab=Approvals"
            className={`tb-signups${signupsPending ? ' has-pending' : ''}`}
            title={signupsPending
              ? [
                `${signupsPending} signup${signupsPending === 1 ? '' : 's'} awaiting approval`,
                ...readySignups.map((u) => `• ${u.name || u.email}${u.requested_role_name ? ` · ${formatRole(u.requested_role_name)}` : ''}`),
              ].join('\n')
              : 'No signups waiting for approval'}
            aria-label={`${signupsPending} pending signup${signupsPending === 1 ? '' : 's'} — review`}
          >
            <Icon name="user-check" />
            <span className="tb-signups-label" aria-hidden="true">Approvals</span>
            <span className="tb-signups-count" aria-hidden="true">
              {signupsPending > 99 ? '99+' : signupsPending}
            </span>
          </Link>
        )}

        <DropdownGroup>
          <Dropdown
            panelClassName="dropdown-menu-wide"
            trigger={({ toggle }) => (
              <button className="icon-btn" onClick={toggle} title="Notifications" aria-label="Notifications">
                <Icon name="bell" />
                {unread > 0 && <span className="dot dot-red" />}
              </button>
            )}
          >
            {({ close }) => (<>
            <div className="dropdown-head">
              Notifications
              <button className="link-btn" disabled={markAll.isPending} onClick={() => markAll.mutate()}>
                Mark all read
              </button>
            </div>
            <div className="dd-scroll">
              {notifQuery.isPending && <div className="notif-row"><div className="notif-text">Loading…</div></div>}
              {notifQuery.isError && (
                <div className="notif-row">
                  <div className="notif-text">{friendlyAuthError(notifQuery.error, 'Could not load notifications.')}</div>
                </div>
              )}
              {notifQuery.isSuccess && notifications.length === 0 && (
                <div className="notif-row"><div className="notif-text">No notifications yet.</div></div>
              )}
              {notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={`notif-row${n.unread ? ' unread' : ''}`}
                  onClick={() => { close(); openNotif(n) }}
                >
                  <span className={`notif-icn ${n.color}`}><Icon name={n.icon} /></span>
                  <div className="notif-body">
                    <div className="notif-title">{n.title}</div>
                    {n.text && <div className="notif-text">{n.text}</div>}
                    <div className="notif-time">{n.time}</div>
                  </div>
                </button>
              ))}
            </div>
            <div className="dropdown-foot">
              <Link to="/notifications" onClick={close}>View all</Link>
            </div>
            </>)}
          </Dropdown>

          <div className="topbar-divider" />

          <Dropdown
            trigger={({ toggle }) => (
              <button className="profile-btn" onClick={toggle}>
                <UserAvatar name={name} src={user?.avatar_url} />
                <span className="profile-meta">
                  <span className="profile-name">{name}</span>
                  <span className="profile-role">{role}</span>
                </span>
                <Icon name="chevron-down" className="chev" />
              </button>
            )}
          >
            {({ close }) => (
              <>
                <div className="dropdown-profile">
                  <UserAvatar name={name} src={user?.avatar_url} className="avatar-lg" />
                  <div>
                    <div className="dp-name">{name}</div>
                    <div className="dp-email">{email}</div>
                  </div>
                </div>
                <div className="dropdown-divider" />
                {/* Links shut the menu as they navigate, or it stays open over the new page. */}
                <Link className="dropdown-link" to="/profile" onClick={close}><Icon name="user" />My Profile</Link>
                {can('settings.view') && (
                  <Link className="dropdown-link" to="/settings" onClick={close}><Icon name="settings" />Settings</Link>
                )}
                <Link className="dropdown-link" to="/help" onClick={close}><Icon name="help" />Help Center</Link>
                {/* The guided tour of their role, again (tour/tours.js); only roles that have one. */}
                {!preview && roleHasTour(user?.role_name) && (
                  <button type="button" className="dropdown-link" onClick={() => { close(); replayTour() }}>
                    <Icon name="sparkles" />Take the tour
                  </button>
                )}
                {/* Phones: the header has no room for the theme button, so it lives here (mobile.css). */}
                <button type="button" className="dropdown-link dd-theme" onClick={toggleTheme} aria-pressed={theme === 'dark'}>
                  <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </button>
                {/* Everyone but a System Administrator just sees their own role — no list. */}
                {!canPreview && (
                  <>
                    <div className="dropdown-divider" />
                    <div className="dropdown-link dd-own-role" aria-label="Your role">
                      <Icon name="shield" />{role}
                    </div>
                    {preview && (
                      <button type="button" className="dropdown-link" onClick={stopPreview}>
                        <Icon name="x" />Exit role view
                      </button>
                    )}
                  </>
                )}
                {canPreview && (
                  <>
                    <div className="dropdown-divider" />
                    <button
                      type="button"
                      className="dropdown-link"
                      aria-expanded={rolesOpen}
                      onClick={() => setRolesOpen((o) => !o)}
                    >
                      <Icon name="shield" />Roles
                      <Icon name="chevron-down" className={`dd-roles-chev${rolesOpen ? ' open' : ''}`} />
                    </button>
                    {rolesOpen && (
                      <div className="dd-roles" role="group" aria-label="View the portal as a role">
                        {rolesQuery.isPending && <div className="dd-roles-note">Loading roles…</div>}
                        {rolesQuery.isError && (
                          <div className="dd-roles-note">
                            {friendlyAuthError(rolesQuery.error, 'Could not load roles.')}
                          </div>
                        )}
                        {rolesQuery.isSuccess && rolesQuery.data.length === 0 && (
                          <div className="dd-roles-note">No active roles.</div>
                        )}
                        {rolesQuery.data?.map((r) => {
                          const current = r.id === user?.role_id
                          return (
                            <button
                              key={r.id}
                              type="button"
                              className={`dropdown-link dd-role${current ? ' current' : ''}`}
                              aria-current={current ? 'true' : undefined}
                              disabled={switchingTo != null}
                              onClick={() => pickRole(r, close)}
                            >
                              <span className="dd-role-name">{formatRole(r.role_name)}</span>
                              {r.id === realRoleId && <span className="dd-role-tag">Yours</span>}
                              {current && preview && <span className="dd-role-tag dd-role-tag-live">Viewing</span>}
                              {switchingTo === r.id && <span className="dd-role-tag">Opening…</span>}
                            </button>
                          )
                        })}
                      </div>
                    )}
                    {preview && (
                      <button type="button" className="dropdown-link" onClick={stopPreview}>
                        <Icon name="x" />Exit role view
                      </button>
                    )}
                  </>
                )}
                <div className="dropdown-divider" />
                <button className="dropdown-link danger" onClick={signOut}>
                  <Icon name="logout" />Sign out
                </button>
              </>
            )}
          </Dropdown>
        </DropdownGroup>
      </div>
    </header>
  )
}
